import { sha256Hex } from '../entropy/hash';
import { classifyRandomization, isProtected, RANDOMIZATION_TEXT, type KnownAnswer } from './randomization';
import type { Collector, Signal } from './types';
import { prop, safe, short, signal, unavailable, withTimeout } from './util';

type OfflineCtor = new (channels: number, length: number, sampleRate: number) => OfflineAudioContext;

function offlineCtor(): OfflineCtor | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.OfflineAudioContext ?? prop<OfflineCtor>(window, 'webkitOfflineAudioContext');
}

function render(ctx: OfflineAudioContext): Promise<AudioBuffer> {
  // Old Safari only fires `oncomplete`; modern browsers also return a promise.
  return withTimeout(
    new Promise<AudioBuffer>((resolve, reject) => {
      ctx.oncomplete = (e) => resolve(e.renderedBuffer);
      const p = ctx.startRendering() as Promise<AudioBuffer> | undefined;
      p?.then(resolve, reject);
    }),
    1500,
    'audio rendering',
  );
}

/**
 * The classic OfflineAudioContext fingerprint: a triangle oscillator through a dynamics compressor,
 * then sum a slice of the output. Floating-point differences in each engine's DSP code show up in
 * the low digits.
 */
async function compressorSum(Ctor: OfflineCtor): Promise<number> {
  const ctx = new Ctor(1, 5000, 44100);
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.value = 10000;
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -50;
  comp.knee.value = 40;
  comp.ratio.value = 12;
  comp.attack.value = 0;
  comp.release.value = 0.25;
  osc.connect(comp);
  comp.connect(ctx.destination);
  osc.start(0);
  const buffer = await render(ctx);
  const data = buffer.getChannelData(0);
  let sum = 0;
  for (let i = 4500; i < 5000; i++) sum += Math.abs(data[i] ?? 0);
  return sum;
}

/** Play a buffer of exactly representable samples and check they come back untouched. */
async function knownAnswer(Ctor: OfflineCtor): Promise<KnownAnswer> {
  const length = 256;
  const ctx = new Ctor(1, length, 44100);
  const input = ctx.createBuffer(1, length, 44100);
  const samples = new Float32Array(length);
  for (let i = 0; i < length; i++) samples[i] = ((i % 8) - 4) / 8; // -0.5 … 0.375, exact in float32
  input.getChannelData(0).set(samples);
  const src = ctx.createBufferSource();
  src.buffer = input;
  src.connect(ctx.destination);
  src.start(0);
  const out = (await render(ctx)).getChannelData(0);
  let allZero = true;
  let mismatches = 0;
  for (let i = 0; i < length; i++) {
    const v = out[i] ?? 0;
    if (v !== 0) allZero = false;
    if (v !== samples[i]) mismatches++;
  }
  if (allZero) return 'blocked';
  return mismatches ? 'altered' : 'ok';
}

let liveContextInfo: Promise<{ sampleRate: number; baseLatency: number | null; outputLatency: number | null } | null> | undefined;

/** Memoized: a live AudioContext is created once per page load, while the click's user activation is fresh. */
function liveContext() {
  liveContextInfo ??= (async () => {
    const Ctor = window.AudioContext ?? prop<typeof AudioContext>(window, 'webkitAudioContext');
    if (!Ctor) return null;
    const ctx = new Ctor();
    const info = {
      sampleRate: ctx.sampleRate,
      baseLatency: typeof ctx.baseLatency === 'number' ? ctx.baseLatency : null,
      outputLatency: typeof ctx.outputLatency === 'number' ? ctx.outputLatency : null,
    };
    await ctx.close().catch(() => undefined);
    return info;
  })().catch(() => null);
  return liveContextInfo;
}

export const audioCollector: Collector = {
  id: 'audio',
  title: 'Audio',
  description: 'How your browser and OS process sound. Nothing is played or recorded; the audio is rendered silently in memory.',
  async collect() {
    const Ctor = offlineCtor();
    if (!Ctor) {
      return [
        unavailable('audio.hash', 'Audio processing fingerprint', 'Web Audio API not available'),
        unavailable('audio.sampleRate', 'Output sample rate', 'Web Audio API not available'),
        unavailable('audio.baseLatency', 'Output latency', 'Web Audio API not available'),
        signal('audio.randomization', 'Randomization check', 'unavailable', { display: RANDOMIZATION_TEXT.unavailable }),
      ];
    }

    let first: number | null = null;
    let second: number | null = null;
    let error = '';
    try {
      first = await compressorSum(Ctor);
      second = await compressorSum(Ctor);
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    }
    const ka = await knownAnswer(Ctor).catch((): KnownAnswer => 'unavailable');
    const state = classifyRandomization({
      first: first === null ? null : String(first),
      second: second === null ? null : String(second),
      knownAnswer: ka,
    });

    const hashSignal = safe('audio.hash', 'Audio processing fingerprint', async (): Promise<Signal> => {
      if (first === null) return unavailable('audio.hash', 'Audio processing fingerprint', error || 'Rendering failed');
      const hash = await sha256Hex(String(first));
      const value = { sum: String(first), sha256: hash };
      const display = `sum ${String(first)} · ${short(hash)}…`;
      if (state === 'blocked') return { ...signal('audio.hash', 'Audio processing fingerprint', value, { display }), available: false, bits: 0.3, note: 'Audio readback blocked.' };
      if (isProtected(state))
        return signal('audio.hash', 'Audio processing fingerprint', value, {
          display,
          bits: 0,
          randomized: true,
          note: 'Randomized by your browser, so it is not a stable identifier.',
        });
      return signal('audio.hash', 'Audio processing fingerprint', value, { display });
    });

    const live = await liveContext();
    return Promise.all([
      hashSignal,
      safe('audio.sampleRate', 'Output sample rate', () =>
        live
          ? signal('audio.sampleRate', 'Output sample rate', live.sampleRate, {
              display: `${live.sampleRate.toLocaleString()} Hz`,
              note: 'Follows your current output device (speakers vs. headphones can differ).',
            })
          : unavailable('audio.sampleRate', 'Output sample rate', 'AudioContext not available'),
      ),
      safe('audio.baseLatency', 'Output latency', () =>
        live && live.baseLatency !== null
          ? signal('audio.baseLatency', 'Output latency', { baseLatency: live.baseLatency, outputLatency: live.outputLatency }, {
              display: `base ${(live.baseLatency * 1000).toFixed(2)} ms${live.outputLatency !== null ? ` · output ${(live.outputLatency * 1000).toFixed(2)} ms` : ''}`,
            })
          : unavailable('audio.baseLatency', 'Output latency', 'baseLatency not supported in this browser'),
      ),
      Promise.resolve(
        signal('audio.randomization', 'Randomization check', { state, knownAnswer: ka, repeatMatched: first === second }, {
          display: RANDOMIZATION_TEXT[state],
        }),
      ),
    ]);
  },
};
